import { styled } from '@mui/system';
import { Typography, Paper, Box, Link, Chip, IconButton } from '@mui/material';

// Container styles
export const StyledContainer = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  margin: 0,
  padding: 0,
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  overflowX: 'hidden',
}));

export const ContentWrapper = styled('div')(({ theme }) => ({
  maxWidth: '1200px',
  margin: '20px auto',
  padding: '0 20px',
  [theme.breakpoints.down('sm')]: {
    padding: '0 15px',
  },
}));

export const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: '50px',
  backgroundColor: '#FFFFFF',
  borderRadius: '16px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  position: 'relative',
  overflow: 'hidden',
  marginBottom: '40px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '6px',
    background: 'linear-gradient(90deg, #3a7bd5, #00d2ff)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '30px 20px',
  },
}));

// Event Header styles
export const EventTitle = styled(Typography)(({ theme }) => ({
  color: '#1a1a1a',
  fontWeight: 800,
  fontSize: '3rem',
  marginBottom: '10px',
  textAlign: 'center',
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  lineHeight: 1.2,
  background: 'linear-gradient(135deg, #3a7bd5, #00d2ff)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  [theme.breakpoints.down('sm')]: {
    fontSize: '2.2rem',
  },
}));

export const EventSummary = styled(Typography)(({ theme }) => ({
  color: '#555555',
  fontWeight: 500,
  fontSize: '1.4rem',
  marginBottom: '20px', // Reduced from 40px to give space for action buttons
  textAlign: 'center',
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  maxWidth: '800px',
  margin: '0 auto 20px auto', // Reduced from 40px
  lineHeight: 1.5,
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.2rem',
    marginBottom: '15px', // Reduced for mobile
  },
}));

// Action Button styles
// ========== REPLACE YOUR EXISTING ActionButtonsContainer WITH THIS ==========

export const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center', // This ensures all buttons are at the same level
  gap: '16px',
  marginBottom: '40px',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    gap: '12px',
    marginBottom: '30px',
    flexDirection: 'row', // Keep horizontal on mobile too
    justifyContent: 'center',
    '& > *': {
      minWidth: 'auto',
    },
  },
}));

export const ActionButton = styled(IconButton)(({ theme, active }) => ({
  backgroundColor: active ? '#e8f5fd' : '#f8fafc',
  color: active ? '#3a7bd5' : '#666666',
  border: `1px solid ${active ? '#3a7bd5' : '#e0e0e0'}`,
  padding: '8px',
  borderRadius: '50%',
  boxShadow: active ? '0 2px 10px rgba(58, 123, 213, 0.2)' : '0 2px 5px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#e8f5fd',
    color: '#3a7bd5',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.07)',
  },
}));

// Event Image styles
export const EventImage = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  height: 'auto',
  borderRadius: '12px',
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
  objectFit: 'contain',
  display: 'block',
  margin: '0 auto',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.01)',
  },
}));

// Section styles
export const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#1a1a1a',
  fontWeight: 700,
  fontSize: '1.3rem',
  marginBottom: '15px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  '& svg': {
    marginRight: '12px',
    color: '#3a7bd5',
    fontSize: '1.6rem',
  },
}));

export const SectionBody = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: '#444444',
  lineHeight: 1.8,
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  whiteSpace: 'pre-wrap',
  marginBottom: '20px',
  textAlign: 'center',
}));

export const DetailsContainer = styled(Box)(({ theme }) => ({
  padding: '35px',
  backgroundColor: '#f8fafc',
  borderRadius: '12px',
  boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.03)',
  marginTop: '40px',
  [theme.breakpoints.down('sm')]: {
    padding: '25px 20px',
  },
}));

// Info Box styles
export const InfoBox = styled(Box)(({ theme }) => ({
  padding: '25px',
  backgroundColor: '#f8fafc',
  borderRadius: '12px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.03)',
  height: '100%',
  '& .MuiSvgIcon-root': {
    color: '#3a7bd5',
  },
}));

export const InfoItem = styled(Box)(({ theme }) => ({
  marginBottom: '25px',
  '&:last-child': {
    marginBottom: 0,
  },
}));

export const StyledLink = styled(Link)(({ theme }) => ({
  color: '#3a7bd5',
  fontWeight: 500,
  textDecoration: 'none',
  wordBreak: 'break-all',
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  transition: 'color 0.2s',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  '&:hover': {
    color: '#00d2ff',
    textDecoration: 'underline',
  },
}));

// Map styles
export const MapContainer = styled(Box)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: '400px',
  [theme.breakpoints.down('md')]: {
    minHeight: '350px',
  },
}));

export const MapHeaderContainer = styled(Box)(({ theme }) => ({
  padding: '16px',
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e8eef3',
}));

export const MapFrame = styled('iframe')(({ theme }) => ({
  border: 0,
  width: '100%',
  height: '100%',
  minHeight: '350px',
  flexGrow: 1,
}));

// Chip/Status styles
export const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: '30px',
  fontSize: '0.9rem',
  borderRadius: '50px',
  padding: '0 10px',
  backgroundColor: '#e1f5fe',
  color: '#0288d1',
  boxShadow: '0 2px 5px rgba(2, 136, 209, 0.15)',
  '& .MuiChip-icon': {
    color: '#0288d1',
  },
}));

// ========== ADD THESE STYLES TO THE END OF YOUR EXISTING styles.js FILE ==========

// Chat Section styles
export const ChatSectionContainer = styled(Box)(({ theme }) => ({
  marginTop: '40px',
  marginBottom: '40px',
}));

export const ChatSectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: '24px',
  textAlign: 'center',
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
  fontWeight: 600,
  color: '#1a1a1a',
  fontSize: '1.8rem',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
    marginBottom: '20px',
  },
}));

export const ChatGridContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '24px',
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
  },
}));

export const ChatBoxWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  '& .chat-title': {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'block',
      marginBottom: '16px',
      textAlign: 'center',
      fontFamily: "'Montserrat', 'Poppins', sans-serif",
      fontWeight: 600,
      color: '#1a1a1a',
      fontSize: '1.3rem',
    },
  },
}));

// Chat Box Component styles
export const ChatContainer = styled(Paper)(({ theme }) => ({
  height: '400px',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e0e0e0',
}));

export const ChatHeader = styled(Box)(({ theme, chatType }) => ({
  padding: '16px 20px',
  backgroundColor: chatType === 'interested' ? '#e8f5fd' : '#e8f5e8',
  borderBottom: '1px solid #e0e0e0',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

export const ChatTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
}));

export const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  backgroundColor: '#fafafa',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#c1c1c1',
    borderRadius: '3px',
  },
}));

export const MessageInputContainer = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  backgroundColor: '#ffffff',
  borderTop: '1px solid #e0e0e0',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

export const MessageItem = styled(Box)(({ theme, isOwnMessage }) => ({
  padding: '8px 16px',
  alignItems: 'flex-start',
  backgroundColor: isOwnMessage ? '#e3f2fd' : 'transparent',
  '&:hover': {
    backgroundColor: isOwnMessage ? '#e3f2fd' : '#f5f5f5',
  },
  display: 'flex',
  gap: '12px',
  marginBottom: '8px',
}));

export const MessageContent = styled(Box)(({ theme }) => ({
  maxWidth: '100%',
  wordBreak: 'break-word',
  flex: 1,
}));

export const MessageText = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  lineHeight: 1.4,
  marginBottom: '4px',
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
}));

export const MessageTime = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: '#888',
  fontStyle: 'italic',
}));

export const MessageAvatar = styled(Box)(({ theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: '#e0e0e0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.8rem',
  fontWeight: 'bold',
  color: '#666',
  flexShrink: 0,
}));

// Updated ActionButtonsContainer for better layout
export const UpdatedActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '20px',
  marginBottom: '40px',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    gap: '15px',
    marginBottom: '30px',
    '& > *': {
      minWidth: 'auto',
    },
  },
}));